import React from "react";
import { MapPin, Compass, Mountain, Users, Sparkles, Stamp } from "lucide-react";

const ICONS = { MapPin, Compass, Mountain, Users, Sparkles };

const ORDER = ["location", "activity", "difficulty", "social", "contribution"];

/**
 * Adventure Passport showcase. Renders a category-grouped grid of stamps.
 * Empty categories show subtle placeholders that hint at what unlocks them.
 */
export default function PassportShowcase({ passport, compact = false }) {
    if (!passport) return null;
    const { categories, stamps, counts, total } = passport;

    return (
        <div data-testid="passport-showcase" className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h2 className="font-display font-black text-2xl text-midnight-pine tracking-tight">Adventure Passport</h2>
                    <p className="text-xs text-slate-gray mt-0.5">Stamps you've collected as you explore.</p>
                </div>
                <div className="text-right">
                    <div className="font-display font-black text-3xl text-ember-orange leading-none">{total}</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">stamps</div>
                </div>
            </header>

            {ORDER.map((kind) => {
                const cat = categories?.[kind];
                if (!cat) return null;
                const Icon = ICONS[cat.icon] || Stamp;
                const items = stamps?.[kind] || [];
                return (
                    <section key={kind} data-testid={`passport-cat-${kind}`} className="bg-snowcap border border-ridge-line rounded-xl p-5">
                        <header className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-ember-orange" />
                                <h3 className="font-display font-bold text-base text-midnight-pine">{cat.label}</h3>
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">{counts?.[kind] || 0} stamps</span>
                        </header>
                        {items.length === 0 ? (
                            <EmptyHint kind={kind} />
                        ) : (
                            <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"}`}>
                                {items.map((s) => (
                                    <StampCard key={s.id} stamp={s} />
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

function StampCard({ stamp }) {
    const date = stamp.created_at ? new Date(stamp.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
    return (
        <div data-testid={`stamp-${stamp.kind}-${stamp.value}`} className="relative group bg-trail-tan/30 border-2 border-dashed border-trail-tan rounded-lg p-3 hover:border-ember-orange transition-all overflow-hidden">
            {/* postmark-style ring */}
            <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full border-2 border-ember-orange/30 opacity-30 pointer-events-none" />
            <Stamp className="w-5 h-5 text-ember-orange mb-1" />
            <div className="text-sm font-bold text-midnight-pine capitalize leading-snug">{stamp.label}</div>
            {date && <div className="text-[10px] text-slate-gray mt-1">{date}</div>}
        </div>
    );
}

function EmptyHint({ kind }) {
    const HINTS = {
        location: "Post adventures from new places to start collecting region stamps.",
        activity: "Try a new adventure type — hiking, biking, climbing, kayaking, and more.",
        difficulty: "Tackle your first easy, moderate, hard, and expert trails.",
        social: "Tag friends and invite others to unlock social milestones.",
        contribution: "Leave trail notes, write reviews, and report unsafe info.",
    };
    return (
        <p className="text-xs text-slate-gray italic px-3 py-3 rounded-md bg-trail-tan/20 border border-dashed border-trail-tan">
            {HINTS[kind] || "Earn stamps as you explore."}
        </p>
    );
}
